import React, { useContext } from 'react';
import { Calendar, MapPin, Clock, ChevronDown, Navigation, Bus, GlassWater, Utensils, Music, Cake, Users, Church } from 'lucide-react';
import { Link } from 'react-router-dom';
import { LanguageContext } from '../LanguageContext';
import Footer from './Footer';

const Home: React.FC = () => {
    const { language } = useContext(LanguageContext);

    // Text content for both languages - now includes all bilingual content
    const text = {
        ro: {
            invitation: "Vă invităm la nunta noastră în data de",
            date: "07 iunie 2025",
            ceremony: "Cununie religioasă",
            reception: "Petrecere",
            specialDay: "Ziua noastră specială",
            ceremonyTitle: "Cununie religioasă",
            ceremonyDescription: "Ceremonia religioasă va avea loc la:",
            receptionTitle: "Petrecere",
            receptionDescription: "Urmată de o petrecere pe malul lacului la:",
            schedule: "Program",
            religiousCeremony: "Cununie religioasă",
            welcomeDrinks: "Întâmpinare și aperitive",
            dinner: "Cină",
            celebration: "Petrecere",
            rsvpTitle: "RSVP",
            rsvpDescription: "Vă rugăm să confirmați prezența până pe 15 aprilie",
            rsvpButton: "Confirmați acum",
            footer: "Abia așteptăm să sărbătorim împreună cu voi!",
            contact: "Pentru orice întrebări, vă rugăm să ne contactați la",
            joinUs: "Alăturați-vă nouă",
            scrollDown: "Derulați în jos pentru mai multe detalii",
            getDirections: "Obțineți indicații",

            // Timeline section
            timelineItems: [
                { time: "14:00", icon: "Clock", event: "Ceremonia la Catedrala Sf. Iosif București" },
                { time: "15:15", icon: "Bus", event: "Transport spre Crevedia din fața catedralei" },
                { time: "16:00", icon: "GlassWater", event: "Start Garden Party" },
                { time: "16:30", icon: "Users", event: "Intrarea Mirilor" },
                { time: "17:00", icon: "Utensils", event: "Aperitive" },
                { time: "18:00", icon: "Music", event: "Dansul Mirilor" },
                { time: "18:30", icon: "Utensils", event: "Felul Principal" },
                { time: "20:00", icon: "Utensils", event: "Sarmale" },
                { time: "22:00", icon: "Cake", event: "Tort" },
                { time: "23:00", icon: "Bus", event: "Primul transport către București" },
                { time: "00:30", icon: "Bus", event: "Al doilea transport către București" },
                { time: "2:00", icon: "Bus", event: "Ultimul transport către București" }
            ],

            // Guest Guide section
            guestGuide: "Ghid pentru invitați",
            dressCode: "Cod vestimentar",
            forLadies: "Pentru doamne",
            ladiesGuide: "Vă recomandăm rochii cu elemente florale, materiale fluide și tulle. Pentru ceremonia religioasă, vă sugerăm să purtați o pălărie sau un accesoriu pentru cap, potrivit pentru biserică.",
            forGentlemen: "Pentru domni",
            gentlemenGuide: "Recomandăm costume în culori deschise (bej, bleu, gri deschis) sau închise (bleumarin, gri închis), însoțite de o cămașă albă sau în culori pastelate. O cravată sau un papion cu accente florale va completa elegant ținuta.",
            practicalTips: "Sfaturi practice",
            tips: [
                "Vă rugăm să ajungeți la biserică cu 15-20 de minute înainte de începerea ceremoniei.",
                "Petrecerea va avea loc într-o zonă cu iarbă, așa că încălțămintea cu toc foarte înalt ar putea fi incomodă. Luați în considerare tocuri mai groase sau aduceți o pereche de încălțăminte de schimb pentru dans.",
                "Fiind iunie, vremea poate fi caldă. Locația petrecerii oferă atât spații umbrite, cât și zone însorite, așa că un accesoriu precum un evantai ar putea fi util.",
            ],
            inspirationText: "Pentru inspirație, vă invităm să consultați",
            moodBoard: "tabloul nostru de inspirație"
        },
        en: {
            invitation: "We invite you to our wedding on",
            date: "June 7th, 2025",
            ceremony: "Ceremony",
            reception: "Reception",
            specialDay: "Our Special Day",
            ceremonyTitle: "Ceremony",
            ceremonyDescription: "The religious ceremony will take place at:",
            receptionTitle: "Reception",
            receptionDescription: "Followed by a garden party by the lake at:",
            schedule: "Event Schedule",
            religiousCeremony: "Religious Ceremony",
            welcomeDrinks: "Welcome Drinks",
            dinner: "Dinner",
            celebration: "Celebration",
            rsvpTitle: "RSVP",
            rsvpDescription: "Please confirm your attendance by April 15th",
            rsvpButton: "RSVP Now",
            footer: "We can't wait to celebrate with you!",
            contact: "For any questions, please contact us at",
            joinUs: "Join us",
            scrollDown: "Scroll down for more details",
            getDirections: "Get directions",

            // Timeline section
            timelineItems: [
                { time: "14:00", icon: "Clock", event: "Wedding Ceremony" },
                { time: "15:15", icon: "Bus", event: "Transport to Crevedia" },
                { time: "16:00", icon: "GlassWater", event: "Start Garden Party" },
                { time: "16:30", icon: "Users", event: "Bride and Groom entrance and small Toast" },
                { time: "17:00", icon: "Utensils", event: "Appetizers" },
                { time: "18:00", icon: "Music", event: "Bride and Groom first dance" },
                { time: "18:30", icon: "Utensils", event: "Main Course" },
                { time: "20:00", icon: "Utensils", event: "Sarmale" },
                { time: "22:00", icon: "Cake", event: "Wedding Cake" },
                { time: "23:00", icon: "Bus", event: "First Transport to Bucharest" },
                { time: "00:30", icon: "Bus", event: "Second Transport to Bucharest" },
                { time: "2:00", icon: "Bus", event: "Final Transport to Bucharest" }
            ],

            // Guest Guide section
            guestGuide: "Guest Guide",
            dressCode: "Dress Code",
            forLadies: "For ladies",
            ladiesGuide: "We recommend dresses with floral elements, flowing materials, and tulle. For the religious ceremony, we suggest wearing a hat or headpiece suitable for church.",
            forGentlemen: "For gentlemen",
            gentlemenGuide: "We recommend suits in light colors (beige, light blue, light gray) or dark colors (navy, dark gray), paired with a white or pastel-colored shirt. A tie or bow tie with floral accents will elegantly complete the outfit.",
            practicalTips: "Practical Tips",
            tips: [
                "Please arrive at the church 15-20 minutes before the ceremony begins.",
                "The party will take place in a grassy area, so very high heels might be uncomfortable. Consider block heels or bring a change of shoes for dancing.",
                "Being June, the weather might be warm. The party venue offers both shaded and sunny areas, so an accessory like a fan might be useful.",
                "We recommend using our provided transportation to get to the party venue. If you choose to come by personal car, please let us know in advance so we can reserve parking for you."
            ],
            inspirationText: "For inspiration, we invite you to check out our",
            moodBoard: "mood board"
        }
    };

    // Function to get the right icon component based on icon name from text
    const getIconComponent = (iconName: string) => {
        const icons = {
            Clock: Clock,
            Bus: Bus,
            GlassWater: GlassWater,
            Users: Users,
            Utensils: Utensils,
            Music: Music,
            Cake: Cake
        };
        return icons[iconName as keyof typeof icons];
    };

    return (
        <div className="min-h-screen pt-20 bg-[#f9f5f2]">
            {/* Hero Section with elegant styling matching invitation */}
            <section className="relative h-screen flex items-center justify-center overflow-hidden">
                <div className="text-center z-10 px-6 max-w-2xl mx-auto">
                    <div className="w-full max-w-2xl mx-auto mb-8">
                        <img src="/images/names-header.png" alt="Adina & Răzvan" className="w-full h-auto" />
                    </div>
                    <p className="text-xl md:text-2xl font-serif text-gray-700 mb-8">
                        {text[language].invitation}
                    </p>
                    <p className="text-3xl md:text-4xl font-serif font-bold text-gray-800 mb-8">
                        {text[language].date}
                    </p>
                    <div className="flex flex-col md:flex-row items-center justify-center gap-6 mt-10">
                        <div className="flex flex-col items-center">
                            <div className="flex items-center bg-white px-5 py-3 rounded-lg shadow-md text-gray-700 mb-2">
                                <Church className="w-5 h-5 mr-2" />
                                <span>{text[language].ceremony}: 14:00</span>
                            </div>
                            <a
                                href="https://maps.app.goo.gl/XnzJU9ENvisTxNn96"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors duration-300 text-sm"
                            >
                                <Navigation className="w-3 h-3 mr-1" />
                                {text[language].getDirections}
                            </a>
                        </div>
                        <div className="flex flex-col items-center">
                            <div className="flex items-center bg-white px-5 py-3 rounded-lg shadow-md text-gray-700 mb-2">
                                <Cake className="w-5 h-5 mr-2" />
                                <span>{text[language].reception}: 16:00</span>
                            </div>
                            <a
                                href="https://maps.app.goo.gl/2CCcnH8Xdrq5Bu84A"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors duration-300 text-sm"
                            >
                                <Navigation className="w-3 h-3 mr-1" />
                                {text[language].getDirections}
                            </a>
                        </div>
                    </div>

                    {/* Scroll indicator */}
                    <div className="absolute bottom-10 left-0 right-0 flex flex-col items-center animate-bounce">
                        <p className="text-gray-600 mb-2">{text[language].scrollDown}</p>
                        <ChevronDown className="w-6 h-6 text-gray-600" />
                    </div>
                </div>
                <div className="absolute bottom-0 left-0 w-40 h-40">
                    <Cake className="w-full h-full opacity-70 text-gray-300" />
                </div>
                <div className="absolute bottom-0 right-0 w-40 h-40">
                    <GlassWater className="w-full h-full opacity-70 text-gray-300" />
                </div>
            </section>

            {/* Timeline Section */}
            <section className="max-w-3xl mx-auto my-16 px-6">
                <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
                    <h2 className="text-3xl font-serif text-center text-gray-800 mb-8 border-b border-gray-200 pb-4">{text[language].schedule}</h2>

                    <div className="space-y-8 mt-8">
                        {text[language].timelineItems.map((item, index) => {
                            const IconComponent = getIconComponent(item.icon);
                            const isLastItem = index === text[language].timelineItems.length - 1;

                            return (
                                <div className="flex" key={index}>
                                    <div className="flex flex-col items-center mr-6">
                                        <div className="rounded-full h-12 w-12 flex items-center justify-center bg-gray-100 text-gray-700 border border-gray-300">
                                            <IconComponent className="w-6 h-6" />
                                        </div>
                                        {!isLastItem && <div className="h-full w-0.5 bg-gray-200 mt-2"></div>}
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-serif text-gray-800">{item.time}</h3>
                                        <p className="text-gray-700">{item.event}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Event Guide Section */}
            <section className="max-w-3xl mx-auto my-16 px-6">
                <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
                    <h2 className="text-3xl font-serif text-center text-gray-800 mb-8 border-b border-gray-200 pb-4">
                        {text[language].guestGuide}
                    </h2>

                    <div className="space-y-8">
                        {/* Dress Code */}
                        <div className="bg-gray-50 rounded-lg p-6">
                            <h3 className="text-2xl font-serif text-gray-800 mb-4">
                                {text[language].dressCode}
                            </h3>

                            {/* Women's dress code */}
                            <div className="mb-6">
                                <div className="flex items-center mb-2">
                                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mr-3">
                                        <span className="text-lg text-gray-700">👗</span>
                                    </div>
                                    <h4 className="text-xl font-medium text-gray-800">
                                        {text[language].forLadies}
                                    </h4>
                                </div>
                                <p className="text-gray-700 pl-11">
                                    {text[language].ladiesGuide}
                                </p>
                            </div>

                            {/* Men's dress code */}
                            <div>
                                <div className="flex items-center mb-2">
                                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mr-3">
                                        <span className="text-lg text-gray-700">👔</span>
                                    </div>
                                    <h4 className="text-xl font-medium text-gray-800">
                                        {text[language].forGentlemen}
                                    </h4>
                                </div>
                                <p className="text-gray-700 pl-11">
                                    {text[language].gentlemenGuide}
                                </p>
                            </div>
                        </div>

                        {/* Practical Tips */}
                        <div className="bg-gray-50 rounded-lg p-6">
                            <h3 className="text-2xl font-serif text-gray-800 mb-4">
                                {text[language].practicalTips}
                            </h3>

                            <ul className="space-y-4 text-gray-700">
                                {text[language].tips.map((tip, index) => (
                                    <li className="flex" key={index}>
                                        <div className="w-8 h-8 rounded-full bg-gray-100 flex-shrink-0 flex items-center justify-center mr-3">
                                            <span className="text-lg text-gray-700">{["🕒", "👞", "☀️"][index]}</span>
                                        </div>
                                        <p>{tip}</p>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Inspiration */}
                        <div className="text-center">
                            <p className="text-gray-700 mb-4">
                                {text[language].inspirationText}
                            </p>
                            <a
                                href="https://pin.it/352nTwnJD"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 transition-colors duration-300"
                            >
                                <span className="mr-2">Pinterest</span>
                                {text[language].moodBoard}
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* RSVP Reminder */}
            <section className="max-w-2xl mx-auto my-16 px-6 text-center">
                <div className="bg-white rounded-lg shadow-lg p-8 relative overflow-hidden">
                    <div className="absolute bottom-0 right-0 w-24 h-24 opacity-10">
                        <img src="/images/flowers.svg" alt="" className="w-full h-full object-contain" />
                    </div>
                    <h2 className="text-3xl font-serif text-gray-800 mb-6">{text[language].rsvpTitle}</h2>
                    <p className="text-gray-700 mb-6">{text[language].rsvpDescription}</p>
                    <Link to="/rsvp">
                        <button className="bg-gray-800 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors duration-300">
                            {text[language].rsvpButton}
                        </button>
                    </Link>
                </div>
            </section>

            <Footer/>
        </div>
    );
};

export default Home;